import { create } from 'zustand';
import type {
  PhageSummary,
  PhageFull,
  ViewMode,
  ReadingFrame,
  Theme,
  SimulationId,
  SimState,
} from '@phage-explorer/core';
import {
  HOLOGRAPHIC_THEME,
  getNextTheme,
  getThemeById,
  nextSpeed,
  prevSpeed,
} from '@phage-explorer/core';
import type { GenomeComparisonResult } from '@phage-explorer/comparison';

type OverlayData = Partial<Record<
  'gcSkew'
  | 'complexity'
  | 'bendability'
  | 'promoter'
  | 'repeats'
  | 'kmerAnomaly'
  | 'hgt'
  | 'tropism'
  | 'structureConstraints'
  | 'anomaly'
  | 'dotPlot'
  | 'cgr'
  | 'non-b-dna'
  | 'synteny'
  | 'mosaicRadar',
  unknown>>;

// Overlay states
export type OverlayId =
  | 'help'
  | 'search'
  | 'goto'
  | 'aaKey'
  | 'comparison'
  | 'analysisMenu'
  | 'simulationHub'
  | 'simulationView'
  | 'complexity'
  | 'gcSkew'
  | 'bendability'
  | 'promoter'
  | 'repeats'
  | 'transcriptionFlow'
  | 'pressure'
  | 'stability'
  | 'modules'
  | 'hgt'
  | 'kmerAnomaly'
  | 'foldQuickview'
  | 'commandPalette'
  | 'phasePortrait'
  | 'biasDecomposition'
  | 'crispr'
  | 'dotPlot'
  | 'selectionPressure'
  | 'logo'
  | 'structureConstraints'
  | 'synteny'
  | 'tropism'
  | 'mosaicRadar'
  | 'cgr'
  | 'anomaly'
  | 'non-b-dna'
  | 'hilbert'
  | 'gel'
  | 'welcome'
  | 'tour'
  | 'epistasis';

export type HelpDetailLevel = 'essential' | 'detailed';
export type ExperienceLevel = 'novice' | 'intermediate' | 'power';

// Comparison view tab
export type ComparisonTab = 'summary' | 'kmer' | 'information' | 'correlation' | 'biological' | 'genes' | 'structural' | 'diff';

// Mouse hover info for amino acids
export interface HoveredAminoAcid {
  letter: string;
  name: string;
  threeCode: string;
  property: string;
  position: number; // position in sequence
}

// Store state interface
export interface PhageExplorerState {
  // Phage data
  phages: PhageSummary[];
  currentPhageIndex: number;
  currentPhage: PhageFull | null;
  isLoadingPhage: boolean;
  /**
   * Gene the user has selected, by gene id, or null.
   *
   * Lives here rather than in a web-only store because both surfaces navigate
   * genes and the web app needs it for shareable deep links. It was previously
   * bolted on by `packages/web/src/store/selectedGeneStore.ts`, which declared
   * a module augmentation and patched the fields onto the store at runtime --
   * a pattern that type-checked only because the web package was excluded from
   * the root tsconfig.
   */
  selectedGeneId: number | null;

  // Sequence viewing
  viewMode: ViewMode;
  readingFrame: ReadingFrame;
  scrollPosition: number;
  /** Zoom scale for the web SequenceView renderer (null = not yet initialized) */
  zoomScale: number | null;

  // Diff mode
  diffEnabled: boolean;
  diffReferencePhageId: number | null;
  diffReferenceSequence: string | null;

  // Theme
  currentTheme: Theme;

  // 3D model
  show3DModel: boolean;
  model3DPaused: boolean;
  model3DSpeed: number;
  model3DFullscreen: boolean;
  model3DQuality: 'low' | 'medium' | 'high' | 'ultra';

  // Mouse hover
  mouseX: number;
  mouseY: number;
  hoveredAminoAcid: HoveredAminoAcid | null;

  // Overlays
  overlays: OverlayId[]; // stack, last = top
  overlayData: OverlayData;
  searchQuery: string;
  searchResults: PhageSummary[];
  helpDetail: HelpDetailLevel;
  experienceLevel: ExperienceLevel;
  sessionStart: number;
  recentCommands: string[];

  // Terminal dimensions
  terminalCols: number;
  terminalRows: number;

  // Error state
  error: string | null;

  // Quit confirmation
  quitConfirmPending: boolean;

  // Comparison mode
  comparisonPhageAIndex: number | null;
  comparisonPhageBIndex: number | null;
  comparisonResult: GenomeComparisonResult | null;
  comparisonLoading: boolean;
  comparisonTab: ComparisonTab;
  comparisonSelectingPhage: 'A' | 'B' | null;

  // Simulation state
  activeSimulationId: SimulationId | null;
  simulationState: SimState | null;
  simulationPaused: boolean;
  simulationSpeed: number;

  // Beginner mode (educational layer for non-biologists)
  beginnerModeEnabled: boolean;
  glossarySidebarOpen: boolean;
  activeTourId: string | null;
  completedModules: string[];
  completedTours: string[];
}

// Store actions interface
export interface PhageExplorerActions {
  // Phage navigation
  setPhages: (phages: PhageSummary[]) => void;
  setCurrentPhageIndex: (index: number) => void;
  /** Select a gene by id. Ignores non-integer or negative ids. */
  setSelectedGeneId: (geneId: number | null) => void;
  /** Clear the gene selection. */
  clearSelectedGene: () => void;
  nextPhage: () => void;
  prevPhage: () => void;
  setCurrentPhage: (phage: PhageFull | null) => void;
  setLoadingPhage: (loading: boolean) => void;

  // Sequence viewing
  setViewMode: (mode: ViewMode) => void;
  toggleViewMode: () => void;
  setReadingFrame: (frame: ReadingFrame) => void;
  cycleReadingFrame: () => void;
  setScrollPosition: (position: number) => void;
  scrollBy: (delta: number) => void;
  scrollToStart: () => void;
  scrollToEnd: () => void;
  setZoomScale: (scale: number) => void;
  zoomIn: (factor?: number) => void;
  zoomOut: (factor?: number) => void;

  // Diff mode
  toggleDiff: () => void;
  setDiffReference: (phageId: number | null, sequence: string | null) => void;

  // Theme
  setTheme: (themeId: string) => void;
  cycleTheme: () => void;

  // 3D model
  toggle3DModel: () => void;
  toggle3DModelPause: () => void;
  set3DModelSpeed: (speed: number) => void;
  toggle3DModelFullscreen: () => void;
  cycle3DModelQuality: () => void;

  // Overlays
  openOverlay: (id: OverlayId) => void;
  closeOverlay: (id?: OverlayId) => void;
  toggleOverlay: (id: OverlayId) => void;
  closeAllOverlays: () => void;
  setSearchQuery: (query: string) => void;
  setSearchResults: (results: PhageSummary[]) => void;
  setHelpDetail: (level: HelpDetailLevel) => void;
  setOverlayData: (data: OverlayData) => void;
  setExperienceLevel: (level: ExperienceLevel) => void;
  promoteExperienceLevel: (level: ExperienceLevel) => void;
  addRecentCommand: (id: string) => void;

  // Terminal
  setTerminalSize: (cols: number, rows: number) => void;

  // Mouse
  setMousePosition: (x: number, y: number) => void;
  setHoveredAminoAcid: (aa: HoveredAminoAcid | null) => void;

  // Error
  setError: (error: string | null) => void;

  // Quit confirmation
  setQuitConfirmPending: (pending: boolean) => void;

  // Reset
  reset: () => void;

  // Comparison
  openComparison: () => void;
  closeComparison: () => void;
  setComparisonPhageA: (index: number | null) => void;
  setComparisonPhageB: (index: number | null) => void;
  setComparisonResult: (result: GenomeComparisonResult | null) => void;
  setComparisonLoading: (loading: boolean) => void;
  setComparisonTab: (tab: ComparisonTab) => void;
  nextComparisonTab: () => void;
  prevComparisonTab: () => void;
  startSelectingPhage: (which: 'A' | 'B') => void;
  confirmPhageSelection: (index: number) => void;
  cancelPhageSelection: () => void;
  swapComparisonPhages: () => void;

  // Simulation controls
  launchSimulation: (simId: SimulationId, initialState: SimState) => void;
  closeSimulation: () => void;
  updateSimulationState: (state: SimState) => void;
  toggleSimulationPause: () => void;
  setSimulationPaused: (paused: boolean) => void;
  simulationSpeedUp: () => void;
  simulationSpeedDown: () => void;
  resetSimulation: (initialState: SimState) => void;

  // Beginner mode controls
  toggleBeginnerMode: () => void;
  setBeginnerModeEnabled: (enabled: boolean) => void;
  openGlossary: () => void;
  closeGlossary: () => void;
  startTour: (tourId: string) => void;
  cancelTour: () => void;
  completeTour: (tourId: string) => void;
  completeModule: (moduleId: string) => void;
  resetBeginnerProgress: () => void;
  restoreBeginnerProgress: () => void;
}

// Layout constants
const SIDEBAR_WIDTH = 30;
const HUD_HEIGHT = 4;
const FOOTER_HEIGHT = 2;
const GENE_MAP_HEIGHT = 2;
const BORDER_WIDTH = 2;
const BORDER_HEIGHT = 2;

// Combined store type
export type PhageExplorerStore = PhageExplorerState & PhageExplorerActions;

// Initial state
const initialState: PhageExplorerState = {
  phages: [],
  currentPhageIndex: 0,
  currentPhage: null,
  isLoadingPhage: false,
  selectedGeneId: null,
  viewMode: 'dna',
  readingFrame: 0,
  scrollPosition: 0,
  zoomScale: null,
  diffEnabled: false,
  diffReferencePhageId: null,
  diffReferenceSequence: null,
  currentTheme: HOLOGRAPHIC_THEME,
  show3DModel: true,
  model3DPaused: true, // Paused by default to prevent flickering
  model3DSpeed: 1,
  model3DFullscreen: false,
  model3DQuality: 'medium',
  mouseX: 0,
  mouseY: 0,
  hoveredAminoAcid: null,
  overlays: [],
  overlayData: {},
  searchQuery: '',
  searchResults: [],
  helpDetail: 'essential',
  experienceLevel: 'novice',
  sessionStart: Date.now(),
  recentCommands: [],
  terminalCols: 80,
  terminalRows: 24,
  error: null,
  quitConfirmPending: false,
  comparisonPhageAIndex: null,
  comparisonPhageBIndex: null,
  comparisonResult: null,
  comparisonLoading: false,
  comparisonTab: 'summary',
  comparisonSelectingPhage: null,
  activeSimulationId: null,
  simulationState: null,
  simulationPaused: true,
  simulationSpeed: 1,
  // Beginner mode initial state
  beginnerModeEnabled: false,
  glossarySidebarOpen: false,
  activeTourId: null,
  completedModules: [],
  completedTours: [],
};

// Create the store
export const usePhageStore = create<PhageExplorerStore>((set, get) => ({
  ...initialState,

  // Phage navigation
  setPhages: (phages) => set({ phages }),

  setSelectedGeneId: (geneId) => {
    // Reject anything that cannot be a real gene id rather than storing it and
    // failing later at the lookup.
    if (geneId !== null && (!Number.isSafeInteger(geneId) || geneId < 0)) return;
    set({ selectedGeneId: geneId });
  },

  clearSelectedGene: () => set({ selectedGeneId: null }),

  setCurrentPhageIndex: (index) => {
    const { phages } = get();
    if (index >= 0 && index < phages.length) {
      set({
        currentPhageIndex: index,
        scrollPosition: 0, // Reset scroll when changing phages
      });
    }
  },

  nextPhage: () => {
    const { currentPhageIndex, phages } = get();
    if (currentPhageIndex < phages.length - 1) {
      set({
        currentPhageIndex: currentPhageIndex + 1,
        scrollPosition: 0,
      });
    }
  },

  prevPhage: () => {
    const { currentPhageIndex } = get();
    if (currentPhageIndex > 0) {
      set({
        currentPhageIndex: currentPhageIndex - 1,
        scrollPosition: 0,
      });
    }
  },

  setCurrentPhage: (phage) => set({ currentPhage: phage }),
  setLoadingPhage: (loading) => set({ isLoadingPhage: loading }),

  // Sequence viewing
  setViewMode: (mode) => set({ viewMode: mode, scrollPosition: 0 }),

  toggleViewMode: () => {
    const { viewMode, scrollPosition } = get();
    const nextMode: ViewMode =
      viewMode === 'dna' ? 'aa' :
      viewMode === 'aa' ? 'dual' :
      'dna';
    
    // Preserve approximate visual position
    let newScroll = scrollPosition;
    if (viewMode === 'dna' && nextMode === 'aa') {
      newScroll = Math.floor(scrollPosition / 3);
    } else if (viewMode === 'aa' && nextMode !== 'aa') {
      newScroll = scrollPosition * 3;
    }
    // dual <-> dna shares DNA coords, no change needed

    set({
      viewMode: nextMode,
      scrollPosition: newScroll,
    });
  },

  setReadingFrame: (frame) => set({ readingFrame: frame }),

  cycleReadingFrame: () => {
    const { readingFrame } = get();
    const frames: ReadingFrame[] = [0, 1, 2, -1, -2, -3];
    const currentIndex = frames.indexOf(readingFrame);
    // If not found (shouldn't happen), default to 0
    const nextIndex = (currentIndex + 1) % frames.length;
    set({ readingFrame: frames[nextIndex] });
  },

  setScrollPosition: (position) => {
    set({ scrollPosition: Math.max(0, position) });
  },

  scrollBy: (delta) => {
    const { scrollPosition } = get();
    set({ scrollPosition: Math.max(0, scrollPosition + delta) });
  },

  scrollToStart: () => set({ scrollPosition: 0 }),

  setZoomScale: (scale) => {
    if (!Number.isFinite(scale)) return;
    const clamped = Math.max(0.1, Math.min(4.0, scale));
    const current = get().zoomScale;
    if (current !== null && Math.abs(current - clamped) < 1e-4) return;
    set({ zoomScale: clamped });
  },

  zoomIn: (factor = 1.3) => {
    const current = get().zoomScale ?? 1.0;
    const next = Math.max(0.1, Math.min(4.0, current * factor));
    if (Math.abs(current - next) < 1e-4) return;
    set({ zoomScale: next });
  },

  zoomOut: (factor = 1.3) => {
    const current = get().zoomScale ?? 1.0;
    const next = Math.max(0.1, Math.min(4.0, current / factor));
    if (Math.abs(current - next) < 1e-4) return;
    set({ zoomScale: next });
  },

  scrollToEnd: () => {
    const { currentPhage, viewMode, terminalCols, terminalRows } = get();
    if (!currentPhage?.genomeLength) return;

    const length = viewMode === 'aa'
      ? Math.floor(currentPhage.genomeLength / 3)
      : currentPhage.genomeLength;

    const gridCols = Math.max(1, terminalCols - SIDEBAR_WIDTH - BORDER_WIDTH);
    const gridRows = Math.max(1, terminalRows - HUD_HEIGHT - FOOTER_HEIGHT - GENE_MAP_HEIGHT - BORDER_HEIGHT);
    const charsPerScreen = gridCols * gridRows;
    
    set({ scrollPosition: Math.max(0, length - charsPerScreen) });
  },

  // Diff mode
  toggleDiff: () => {
    const { diffEnabled, phages } = get();
    if (!diffEnabled && phages.length > 0) {
      // Enable diff with first phage (lambda) as reference by default
      const lambdaIndex = phages.findIndex(p =>
        p.slug === 'lambda' || p.name.toLowerCase().includes('lambda')
      );
      const refIndex = lambdaIndex >= 0 ? lambdaIndex : 0;
      set({
        diffEnabled: true,
        diffReferencePhageId: phages[refIndex].id,
      });
    } else {
      set({
        diffEnabled: false,
        diffReferencePhageId: null,
        diffReferenceSequence: null,
      });
    }
  },

  setDiffReference: (phageId, sequence) => set({
    diffReferencePhageId: phageId,
    diffReferenceSequence: sequence,
  }),

  // Theme
  setTheme: (themeId) => set({ currentTheme: getThemeById(themeId) }),

  cycleTheme: () => {
    const { currentTheme } = get();
    set({ currentTheme: getNextTheme(currentTheme.id) });
  },

  // 3D model
  toggle3DModel: () => {
    const { show3DModel } = get();
    set({ show3DModel: !show3DModel });
  },

  toggle3DModelPause: () => {
    const { model3DPaused } = get();
    set({ model3DPaused: !model3DPaused });
  },

  set3DModelSpeed: (speed) => set({ model3DSpeed: speed }),

  toggle3DModelFullscreen: () => {
    const { model3DFullscreen } = get();
    // When entering fullscreen:
    // - Automatically use high quality
    // - Unpause the animation
    // - Close any active overlay (so it doesn't appear when exiting)
    // When exiting:
    // - Return to medium quality
    // - Pause the animation to prevent flickering
    const enteringFullscreen = !model3DFullscreen;
    set({
      model3DFullscreen: enteringFullscreen,
      model3DQuality: enteringFullscreen ? 'high' : 'medium',
      model3DPaused: !enteringFullscreen, // Pause when exiting, unpause when entering
      // Close overlays when entering fullscreen to avoid hidden state
      ...(enteringFullscreen ? { overlays: [], searchQuery: '', searchResults: [] } : {}),
    });
  },

  cycle3DModelQuality: () => {
    const { model3DQuality } = get();
    const qualities: Array<'low' | 'medium' | 'high' | 'ultra'> = ['low', 'medium', 'high', 'ultra'];
    const currentIndex = qualities.indexOf(model3DQuality);
    const nextIndex = (currentIndex + 1) % qualities.length;
    set({ model3DQuality: qualities[nextIndex] });
  },

  setSearchQuery: (query) => set({ searchQuery: query }),
  setSearchResults: (results) => set({ searchResults: results }),

  // Terminal
  setTerminalSize: (cols, rows) => set({ terminalCols: cols, terminalRows: rows }),

  // Mouse
  setMousePosition: (x, y) => set({ mouseX: x, mouseY: y }),
  setHoveredAminoAcid: (aa) => set({ hoveredAminoAcid: aa }),

  // Error
  setError: (error) => set({ error }),

  // Quit confirmation
  setQuitConfirmPending: (pending) => set({ quitConfirmPending: pending }),

  // Reset
  reset: () => set(initialState),

  // Comparison
  openComparison: () => {
    set(state => {
      if (state.phages.length === 0) return {}; // Guard against empty state

      // Default: compare current phage with Lambda or first phage
      const lambdaIndex = state.phages.findIndex(p =>
        p.slug === 'lambda' || p.name.toLowerCase().includes('lambda')
      );
      let defaultB = lambdaIndex >= 0 && lambdaIndex !== state.currentPhageIndex
        ? lambdaIndex
        : state.currentPhageIndex === 0 ? 1 : 0;
      // Avoid comparing a phage with itself when there is more than one phage.
      if (defaultB === state.currentPhageIndex && state.phages.length > 1) {
        defaultB = state.currentPhageIndex === 0 ? 1 : 0;
      }

      return {
        overlays: [...state.overlays.filter(o => o !== 'comparison'), 'comparison'],
        comparisonPhageAIndex: state.currentPhageIndex,
        comparisonPhageBIndex: Math.min(defaultB, state.phages.length - 1),
        comparisonResult: null,
        comparisonTab: 'summary',
        comparisonSelectingPhage: null,
      };
    });
  },

  closeComparison: () => set(state => ({
    overlays: state.overlays.filter(o => o !== 'comparison'),
    comparisonResult: null,
    comparisonSelectingPhage: null,
  })),

  setComparisonPhageA: (index) => set({
    comparisonPhageAIndex: index,
    comparisonResult: null, // Clear result when phages change
  }),

  setComparisonPhageB: (index) => set({
    comparisonPhageBIndex: index,
    comparisonResult: null,
  }),

  setComparisonResult: (result) => set({ comparisonResult: result }),

  setComparisonLoading: (loading) => set({ comparisonLoading: loading }),

  setComparisonTab: (tab) => set({ comparisonTab: tab }),

  nextComparisonTab: () => {
    const { comparisonTab } = get();
    const tabs: ComparisonTab[] = ['summary', 'kmer', 'information', 'correlation', 'biological', 'genes', 'structural', 'diff'];
    const currentIndex = tabs.indexOf(comparisonTab);
    const nextIndex = (currentIndex + 1) % tabs.length;
    set({ comparisonTab: tabs[nextIndex] });
  },

  prevComparisonTab: () => {
    const { comparisonTab } = get();
    const tabs: ComparisonTab[] = ['summary', 'kmer', 'information', 'correlation', 'biological', 'genes', 'structural', 'diff'];
    const currentIndex = tabs.indexOf(comparisonTab);
    const prevIndex = (currentIndex - 1 + tabs.length) % tabs.length;
    set({ comparisonTab: tabs[prevIndex] });
  },

  startSelectingPhage: (which) => set({ comparisonSelectingPhage: which }),

  confirmPhageSelection: (index) => {
    const { comparisonSelectingPhage, comparisonPhageAIndex, comparisonPhageBIndex } = get();
    if (comparisonSelectingPhage === 'A') {
      // Prevent selecting the same phage for both sides.
      const safeIndex = index === comparisonPhageBIndex && index > 0 ? index - 1 : index;
      set({
        comparisonPhageAIndex: safeIndex,
        comparisonSelectingPhage: null,
        comparisonResult: null,
      });
    } else if (comparisonSelectingPhage === 'B') {
      const safeIndex = index === comparisonPhageAIndex && index > 0 ? index - 1 : index;
      set({
        comparisonPhageBIndex: safeIndex,
        comparisonSelectingPhage: null,
        comparisonResult: null,
      });
    }
  },

  cancelPhageSelection: () => set({ comparisonSelectingPhage: null }),

  swapComparisonPhages: () => {
    const { comparisonPhageAIndex, comparisonPhageBIndex } = get();
    set({
      comparisonPhageAIndex: comparisonPhageBIndex,
      comparisonPhageBIndex: comparisonPhageAIndex,
      comparisonResult: null,
    });
  },

  // Overlay manager
  openOverlay: (id) => {
    set(state => {
      const filtered = state.overlays.filter(o => o !== id);
      const next = [...filtered, id];
      return {
        overlays: next.slice(-3),
        ...(id === 'help' ? { helpDetail: 'essential' as HelpDetailLevel } : {}),
      };
    });
  },

  closeOverlay: (id) => {
    set(state => {
      if (id) {
        return { overlays: state.overlays.filter(o => o !== id) };
      }
      return { overlays: state.overlays.slice(0, -1) };
    });
  },

  toggleOverlay: (id) => {
    // Use atomic state update to prevent race conditions
    set(state => {
      if (state.overlays.includes(id)) {
        return { overlays: state.overlays.filter(o => o !== id) };
      }
      // Inline openOverlay logic to avoid nested get() calls
      const filtered = state.overlays.filter(o => o !== id);
      const next = [...filtered, id];
      return {
        overlays: next.slice(-3),
        ...(id === 'help' ? { helpDetail: 'essential' as HelpDetailLevel } : {}),
      };
    });
  },

  closeAllOverlays: () => set({ overlays: [] }),

  setHelpDetail: (level) => set({ helpDetail: level }),
  setOverlayData: (data) => set(state => ({ overlayData: { ...state.overlayData, ...data } })),
  setExperienceLevel: (level) => set({ experienceLevel: level }),
  promoteExperienceLevel: (level) => {
    set(state => {
      const order: ExperienceLevel[] = ['novice', 'intermediate', 'power'];
      const currentIdx = order.indexOf(state.experienceLevel);
      const nextIdx = order.indexOf(level);
      if (nextIdx > currentIdx) {
        return { experienceLevel: level };
      }
      return {};
    });
  },
  addRecentCommand: (id) => {
    set(state => {
      const deduped = [id, ...state.recentCommands.filter(c => c !== id)];
      return { recentCommands: deduped.slice(0, 10) };
    });
  },

  // Simulation controls
  launchSimulation: (simId, initialState) => {
    // Merge overlay change with simulation state to avoid race condition
    set(state => {
      const filtered = state.overlays.filter(o => o !== 'simulationView') as OverlayId[];
      const next: OverlayId[] = [...filtered, 'simulationView'];
      return {
        activeSimulationId: simId,
        simulationState: initialState,
        simulationPaused: false,
        simulationSpeed: 1,
        overlays: next.slice(-3),
      };
    });
  },

  closeSimulation: () => {
    // Merge overlay change with simulation state to avoid race condition
    set(state => ({
      activeSimulationId: null,
      simulationState: null,
      simulationPaused: true,
      simulationSpeed: 1,
      overlays: state.overlays.filter(o => o !== 'simulationView') as OverlayId[],
    }));
  },

  updateSimulationState: (state) => set({ simulationState: state }),

  toggleSimulationPause: () => {
    const { simulationPaused } = get();
    set({ simulationPaused: !simulationPaused });
  },

  setSimulationPaused: (paused) => set({ simulationPaused: paused }),

  simulationSpeedUp: () => {
    const { simulationSpeed } = get();
    set({ simulationSpeed: nextSpeed(simulationSpeed) });
  },

  simulationSpeedDown: () => {
    const { simulationSpeed } = get();
    set({ simulationSpeed: prevSpeed(simulationSpeed) });
  },

  resetSimulation: (initialState) => {
    set({
      simulationState: initialState,
      simulationPaused: true,
    });
  },

  // Beginner mode controls
  toggleBeginnerMode: () => {
    const { beginnerModeEnabled } = get();
    const newValue = !beginnerModeEnabled;
    set({ beginnerModeEnabled: newValue });
    // Persist to localStorage
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('phage-explorer-beginner-mode', JSON.stringify(newValue));
    }
  },

  setBeginnerModeEnabled: (enabled) => {
    set({ beginnerModeEnabled: enabled });
    // Persist to localStorage
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('phage-explorer-beginner-mode', JSON.stringify(enabled));
    }
  },

  openGlossary: () => set({ glossarySidebarOpen: true }),

  closeGlossary: () => set({ glossarySidebarOpen: false }),

  startTour: (tourId) => set({ activeTourId: tourId }),

  cancelTour: () => set({ activeTourId: null }),

  completeTour: (tourId) => {
    const { completedTours, activeTourId } = get();
    const updated = completedTours.includes(tourId)
      ? completedTours
      : [...completedTours, tourId];
    set({
      completedTours: updated,
      // Clear active tour if completing the current one
      activeTourId: activeTourId === tourId ? null : activeTourId,
    });
    // Persist to localStorage
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('phage-explorer-completed-tours', JSON.stringify(updated));
    }
  },

  completeModule: (moduleId) => {
    const { completedModules } = get();
    if (completedModules.includes(moduleId)) return;
    const updated = [...completedModules, moduleId];
    set({ completedModules: updated });
    // Persist to localStorage
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('phage-explorer-completed-modules', JSON.stringify(updated));
    }
  },

  resetBeginnerProgress: () => {
    set({
      completedModules: [],
      completedTours: [],
      activeTourId: null,
    });
    // Clear localStorage
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem('phage-explorer-completed-tours');
      localStorage.removeItem('phage-explorer-completed-modules');
    }
  },

  restoreBeginnerProgress: () => {
    if (typeof localStorage === 'undefined') return;

    // Read localStorage values once (outside state callback since localStorage is synchronous)
    const storedBeginnerMode = localStorage.getItem('phage-explorer-beginner-mode');
    const storedTours = localStorage.getItem('phage-explorer-completed-tours');
    const storedModules = localStorage.getItem('phage-explorer-completed-modules');

    // Use state callback to atomically read and update state
    set(state => {
      let beginnerModeEnabled = state.beginnerModeEnabled;
      let completedTours = state.completedTours;
      let completedModules = state.completedModules;
      let hasChanges = false;

      // Restore beginner mode preference
      if (storedBeginnerMode !== null) {
        try {
          const val = JSON.parse(storedBeginnerMode);
          // Only accept boolean values to prevent type corruption
          if (typeof val === 'boolean' && val !== beginnerModeEnabled) {
            beginnerModeEnabled = val;
            hasChanges = true;
          }
        } catch { /* ignore */ }
      }

      // Restore completed tours
      if (storedTours !== null) {
        try {
          const val = JSON.parse(storedTours);
          if (Array.isArray(val)) {
            // Filter to strings only to prevent type corruption
            const validTours = val.filter((t): t is string => typeof t === 'string');
            // Merge unique
            const next = Array.from(new Set([...completedTours, ...validTours]));
            if (next.length !== completedTours.length) {
              completedTours = next;
              hasChanges = true;
            }
          }
        } catch { /* ignore */ }
      }

      // Restore completed modules
      if (storedModules !== null) {
        try {
          const val = JSON.parse(storedModules);
          if (Array.isArray(val)) {
            // Filter to strings only to prevent type corruption
            const validModules = val.filter((m): m is string => typeof m === 'string');
            const next = Array.from(new Set([...completedModules, ...validModules]));
            if (next.length !== completedModules.length) {
              completedModules = next;
              hasChanges = true;
            }
          }
        } catch { /* ignore */ }
      }

      if (hasChanges) {
        return {
          beginnerModeEnabled,
          completedTours,
          completedModules,
        };
      }
      return {};
    });
  },
}));

// Selector hooks for common derived state
export const useCurrentPhageSummary = () => {
  const phages = usePhageStore((s) => s.phages);
  const index = usePhageStore((s) => s.currentPhageIndex);
  return phages[index] ?? null;
};

export const useGridDimensions = () => {
  const cols = usePhageStore((s) => s.terminalCols);
  const rows = usePhageStore((s) => s.terminalRows);

  return {
    gridCols: Math.max(1, cols - SIDEBAR_WIDTH - BORDER_WIDTH),
    gridRows: Math.max(1, rows - HUD_HEIGHT - FOOTER_HEIGHT - GENE_MAP_HEIGHT - BORDER_HEIGHT),
    sidebarWidth: SIDEBAR_WIDTH,
    hudHeight: HUD_HEIGHT,
    footerHeight: FOOTER_HEIGHT,
    geneMapHeight: GENE_MAP_HEIGHT,
  };
};

export const useOverlayStack = () => usePhageStore((s) => s.overlays);
export const useTopOverlay = () => usePhageStore((s) => s.overlays.at(-1) ?? null);

// Simulation selectors
export const useActiveSimulation = () => usePhageStore((s) => s.activeSimulationId);
export const useSimulationState = () => usePhageStore((s) => s.simulationState);
export const useSimulationPaused = () => usePhageStore((s) => s.simulationPaused);
export const useSimulationSpeed = () => usePhageStore((s) => s.simulationSpeed);
export const useIsSimulationActive = () => usePhageStore((s) => s.activeSimulationId !== null);
export const useExperienceLevel = () => usePhageStore((s) => s.experienceLevel);
export const useHelpDetail = () => usePhageStore((s) => s.helpDetail);

// Beginner mode selectors
export const useBeginnerModeEnabled = () => usePhageStore((s) => s.beginnerModeEnabled);
export const useGlossarySidebarOpen = () => usePhageStore((s) => s.glossarySidebarOpen);
export const useActiveTourId = () => usePhageStore((s) => s.activeTourId);
export const useCompletedModules = () => usePhageStore((s) => s.completedModules);
export const useCompletedTours = () => usePhageStore((s) => s.completedTours);
export const useHasCompletedTour = (tourId: string) => usePhageStore((s) => s.completedTours.includes(tourId));
export const useHasCompletedModule = (moduleId: string) => usePhageStore((s) => s.completedModules.includes(moduleId));
